import React, { useCallback, useRef, useState } from 'react';
import './VideoPlayer.css';

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) {
    return '00:00';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const VideoPlayer = ({
  src,
  poster,
  youtubeId,
  className = '',
  title = 'Video player',
  captionsSrc,
  captionsLang = 'en',
  captionsLabel = 'English',
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen?.();
    }
  }, []);

  const handleSeek = useCallback((event) => {
    const video = videoRef.current;
    if (!video) return;
    const time = Number(event.target.value);
    video.currentTime = time;
    setCurrentTime(time);
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (youtubeId) {
    return (
      <div className={`opv-section ${className}`.trim()}>
        <div className='opv-player'>
          <div className='opv-youtube-wrapper'>
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
              title={title}
              frameBorder='0'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
              className='opv-youtube-iframe'
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`opv-section ${className}`.trim()}>
      <div className='opv-player' ref={containerRef}>
        <video
          ref={videoRef}
          className='opv-video'
          src={src}
          poster={poster}
          aria-label={title}
          playsInline
          preload='metadata'
          onClick={togglePlay}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onVolumeChange={(e) => setIsMuted(e.currentTarget.muted)}
        >
          {captionsSrc && (
            <track
              kind='captions'
              src={captionsSrc}
              srcLang={captionsLang}
              label={captionsLabel}
              default
            />
          )}
        </video>

        <button
          type='button'
          className='opv-volume'
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <svg viewBox='0 0 32 32' fill='none' aria-hidden='true'>
              <path
                d='M4.00011 18.784V13.216C4.00011 12.6613 4.18944 12.1307 4.52677 11.7387C4.86544 11.348 5.32277 11.128 5.80011 11.128H8.32011C8.63611 11.1267 8.94677 11.0307 9.22011 10.848L14.5134 7.34135C14.7854 7.16135 15.0948 7.06802 15.4094 7.06935C15.7241 7.07068 16.0321 7.16802 16.3028 7.35202C16.5761 7.53468 16.8014 7.79735 16.9588 8.11202C17.1161 8.42802 17.1988 8.78668 17.2001 9.15202V22.8493C17.2001 23.2133 17.1161 23.572 16.9588 23.8867C16.8088 24.192 16.5835 24.454 16.3041 24.648C16.0321 24.8307 15.7241 24.928 15.4094 24.9307C15.0899 24.9317 14.7773 24.8369 14.5121 24.6587L9.22011 21.1507C8.95382 20.9718 8.6409 20.8749 8.32011 20.872H5.80011C5.32277 20.872 4.86544 20.652 4.52677 20.26C4.18161 19.8466 3.9949 19.3225 4.00011 18.784Z'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          ) : (
            <svg viewBox='0 0 32 32' fill='none' aria-hidden='true'>
              <path
                d='M4.00011 18.784V13.216C4.00011 12.6613 4.18944 12.1307 4.52677 11.7387C4.86544 11.348 5.32277 11.128 5.80011 11.128H8.32011C8.63611 11.1267 8.94677 11.0307 9.22011 10.848L14.5134 7.34135C14.7854 7.16135 15.0948 7.06802 15.4094 7.06935C15.7241 7.07068 16.0321 7.16802 16.3028 7.35202C16.5761 7.53468 16.8014 7.79735 16.9588 8.11202C17.1161 8.42802 17.1988 8.78668 17.2001 9.15202V22.8493C17.2001 23.2133 17.1161 23.572 16.9588 23.8867C16.8088 24.192 16.5835 24.454 16.3041 24.648C16.0321 24.8307 15.7241 24.928 15.4094 24.9307C15.0899 24.9317 14.7773 24.8369 14.5121 24.6587L9.22011 21.1507C8.95382 20.9718 8.6409 20.8749 8.32011 20.872H5.80011C5.32277 20.872 4.86544 20.652 4.52677 20.26C4.18161 19.8466 3.9949 19.3225 4.00011 18.784Z'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M21.6367 20.9532C22.2987 20.2915 22.8237 19.5059 23.182 18.6412C23.5402 17.7766 23.7246 16.8498 23.7246 15.9139C23.7246 14.9779 23.5402 14.0512 23.182 13.1865C22.8237 12.3218 22.2987 11.5362 21.6367 10.8745M24.6594 24.0532C25.7183 22.9946 26.5582 21.7377 27.1313 20.3544C27.7044 18.9711 27.9993 17.4885 27.9993 15.9912C27.9993 14.4939 27.7044 13.0113 27.1313 11.628C26.5582 10.2447 25.7183 8.98782 24.6594 7.9292'
                stroke='currentColor'
                strokeWidth='2'
                strokeMiterlimit='10'
                strokeLinecap='round'
              />
            </svg>
          )}
        </button>

        <div className='opv-controls'>
          <button
            type='button'
            className='opv-play'
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
                <rect x='6' y='5' width='4' height='14' rx='1' />
                <rect x='14' y='5' width='4' height='14' rx='1' />
              </svg>
            ) : (
              <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
                <path d='M8 5v14l11-7L8 5Z' />
              </svg>
            )}
          </button>

          <span className='opv-time'>{formatTime(currentTime)}</span>

          <input
            type='range'
            className='opv-progress'
            min={0}
            max={duration || 0}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            style={{ '--opv-progress': `${progress}%` }}
            aria-label='Seek'
          />

          <span className='opv-time opv-duration'>{formatTime(duration)}</span>

          <button
            type='button'
            className='opv-fullscreen'
            onClick={toggleFullscreen}
            aria-label='Toggle fullscreen'
          >
            <svg viewBox='0 0 24 24' fill='none' aria-hidden='true'>
              <path
                d='M8 3H3v5 M16 3h5v5 M21 16v5h-5 M3 16v5h5'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
